import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import ArticleDetail, { Article } from '../components/ArticleDetail';
import { useSEO } from '../hooks/useSEO';
import { Bitcoin, TrendingUp, Cpu, BookOpen } from 'lucide-react';

interface ArticlesProps {
  onNavigate: (page: string) => void;
  openArticleId?: string;
}

const categoryIcon = (cat: string) => {
  switch (cat.toLowerCase()) {
    case 'bitcoin': return <Bitcoin size={13} />;
    case 'macro': return <TrendingUp size={13} />;
    case 'ai': return <Cpu size={13} />;
    default: return <BookOpen size={13} />;
  }
};

const ArticleCard: React.FC<{ article: Article; onClick: () => void }> = ({ article, onClick }) => (
  <div
    onClick={onClick}
    className="group border border-white/5 rounded-xl overflow-hidden cursor-pointer relative transition-all flex flex-col"
    style={{
      background: '#111111',
      boxShadow: `0 0 0 1px ${article.categoryColor}00`,
    }}
    onMouseEnter={e => (e.currentTarget.style.boxShadow = `0 0 28px 0 ${article.categoryColor}15, 0 0 0 1px ${article.categoryColor}20`)}
    onMouseLeave={e => (e.currentTarget.style.boxShadow = `0 0 0 1px ${article.categoryColor}00`)}
  >
    {/* Thumbnail image */}
    {article.image ? (
      <div className="w-full overflow-hidden" style={{ height: '200px' }}>
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
    ) : (
      <div
        className="w-full flex items-center justify-center"
        style={{
          height: '200px',
          background: `radial-gradient(ellipse at center, ${article.categoryColor}12 0%, #0f0f0f 70%)`,
        }}
      >
        <span className="text-5xl opacity-20">{article.category === 'Bitcoin' ? '₿' : article.category === 'AI' ? '⚡' : '📈'}</span>
      </div>
    )}

    {/* Content */}
    <div className="p-5 flex flex-col flex-1" style={{ background: `radial-gradient(ellipse at top left, ${article.categoryColor}06 0%, #111111 60%)` }}>
      {/* Meta row */}
      <div className="flex items-center gap-2 mb-3">
        <span
          className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider"
          style={{ backgroundColor: `${article.categoryColor}18`, color: article.categoryColor }}
        >
          {categoryIcon(article.category)}
          {article.category}
        </span>
        <span className="text-xs text-gray-600">{article.date}</span>
      </div>

      {/* Title */}
      <h2 className="text-base font-semibold text-white leading-snug mb-2 group-hover:text-gray-100 transition-colors line-clamp-2">
        {article.title}
      </h2>

      {/* Excerpt */}
      <p className="text-sm text-gray-500 leading-relaxed line-clamp-3 mb-4 flex-1">
        {article.excerpt}
      </p>

      {/* CTA */}
      <div className="flex items-center justify-between mt-auto">
        <span className="text-xs font-medium transition-colors" style={{ color: article.categoryColor }}>
          Read article →
        </span>
      </div>
    </div>
  </div>
);

// Wrapper that applies per-article SEO then renders ArticleDetail
const ArticleDetailWithSEO: React.FC<{ article: Article; onBack: () => void }> = ({ article, onBack }) => {
  const imageUrl = article.image
    ? (article.image.startsWith('http') ? article.image : `https://utxomacro.com/${article.image.replace(/^\//, '')}`)
    : undefined;
  useSEO({
    title: article.title,
    description: article.excerpt ?? article.content?.slice(0, 155).replace(/[#*]/g, '').trim(),
    image: imageUrl,
    url: `https://utxomacro.com/articles/${article.id}`,
    type: 'article',
    publishedAt: article.publishedAt,
    category: article.category,
  });
  return <ArticleDetail article={article} onBack={onBack} />;
};

const Articles: React.FC<ArticlesProps> = ({ onNavigate, openArticleId }) => {
  const { id: routeArticleId } = useParams<{ id: string }>();
  const navigateRouter = useNavigate();
  useSEO({
    title: 'Articles',
    description: 'Bitcoin on-chain analysis, macro intelligence, and AI disruption articles from UTXOMacro.',
    url: 'https://utxomacro.com/articles',
    keywords: 'Bitcoin articles, macro analysis, on-chain, AI disruption, UTXOMacro',
  });
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Article | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [visibleCount, setVisibleCount] = useState(9);

  useEffect(() => {
    setNotFound(false);
    fetch('/data/articles.json')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: Article[]) => {
        // Filter out articles scheduled for a future date
        const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
        const visible = data.filter(a => {
          if (!a.scheduled_for) return true;
          const [y, m, d] = a.scheduled_for.split('-').map(Number);
          return new Date(y, m - 1, d) <= todayStart;
        });
        // Sort newest first
        const sorted = [...visible].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setArticles(sorted);
        setLoading(false);
        // Auto-open article if ID was passed via route param
        if (routeArticleId) {
          const target = sorted.find(a => a.id === routeArticleId);
          if (target) {
            setSelected(target);
          } else {
            // Article ID in URL not found in published data
            setNotFound(true);
          }
        }
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [routeArticleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If an article is selected, show full detail view
  if (selected) {
    return <ArticleDetailWithSEO article={selected} onBack={() => { setSelected(null); navigateRouter('/articles'); }} />;
  }

  // Article ID provided but not found in published data
  if (notFound && routeArticleId) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
        <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full flex flex-col items-center justify-center text-center">
          <div className="text-5xl mb-6 opacity-20">📄</div>
          <h1 className="text-2xl font-bold text-white mb-3">Article not published yet</h1>
          <p className="text-gray-500 text-sm max-w-md mb-8">
            This article hasn't been published to the live site yet. If you're looking for a draft, check the dashboard at{' '}
            <span className="font-mono text-gray-400">localhost:3000</span>.
          </p>
          <button
            onClick={() => navigateRouter('/articles')}
            className="px-5 py-2.5 rounded-xl text-sm font-medium border border-white/10 text-gray-400 hover:text-white hover:border-[#F7931A]/40 transition-all"
          >
            ← Browse published articles
          </button>
        </main>
        <Footer onNavigate={onNavigate} />
      </div>
    );
  }

  const categories = ['All', ...Array.from(new Set(articles.map(a => a.category)))];
  const filtered = activeCategory === 'All' ? articles : articles.filter(a => a.category === activeCategory);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-white mb-1">Articles</h1>
          <div className="w-12 h-0.5 bg-[#F7931A] rounded mb-4" />
          <p className="text-gray-500 text-sm max-w-xl">
            Signal over noise. Deep-dive research on Bitcoin, macro, and AI — grounded in data, built for investors who want to think clearly.
          </p>
        </div>

        {/* Category filter */}
        {!loading && articles.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-8">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setVisibleCount(9); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                  activeCategory === cat
                    ? 'bg-[#F7931A]/15 border-[#F7931A]/40 text-[#F7931A]'
                    : 'border-white/8 text-gray-500 hover:border-white/20 hover:text-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* States */}
        {loading && (
          <div className="flex items-center justify-center py-24 text-gray-600 text-sm">
            Loading articles…
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center justify-center py-24 text-red-500/60 text-sm">
            Failed to load articles: {error}
          </div>
        )}

        {!loading && !error && articles.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-600 text-sm gap-3">
            <BookOpen size={32} className="opacity-30" />
            <p>No articles published yet. Check back after the next content run.</p>
          </div>
        )}

        {/* Article grid */}
        {!loading && !error && filtered.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {visible.map(article => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  onClick={() => {
                    setSelected(article);
                    navigateRouter(`/articles/${article.id}`);
                  }}
                />
              ))}
            </div>

            {/* Show more / Show less */}
            <div className="flex items-center justify-center gap-4 mt-10">
              {hasMore && (
                <button
                  onClick={() => setVisibleCount(c => c + 9)}
                  className="px-6 py-2.5 rounded-xl text-sm font-medium border border-white/10 text-gray-400 hover:text-white hover:border-[#F7931A]/40 hover:bg-[#F7931A]/6 transition-all"
                >
                  Show more ({filtered.length - visibleCount} remaining)
                </button>
              )}
              {visibleCount > 9 && (
                <button
                  onClick={() => setVisibleCount(9)}
                  className="px-6 py-2.5 rounded-xl text-sm font-medium border border-white/8 text-gray-600 hover:text-gray-400 transition-all"
                >
                  Show less
                </button>
              )}
            </div>
          </>
        )}

        {!loading && !error && filtered.length === 0 && articles.length > 0 && (
          <div className="flex items-center justify-center py-16 text-gray-600 text-sm">
            No articles in this category yet.
          </div>
        )}
      </main>

      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default Articles;
